import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { FaArrowLeft, FaCrown } from "react-icons/fa";
import { toast } from "react-toastify";
import Leftsidebar from "../sidebar/leftsidebar";
import LeftResponsivesidebar from "../sidebar/left-responsive";
import Headmenu from "../sidebar/headmenu";
import PlanCard, { planBadge } from "./PlanCard";
import { formatDateOnly } from "../../utils/date";
import {
  PLAN_TYPES,
  cancelScheduledChange,
  cancelSubscription,
  getCurrentSubscription,
  getPlans,
  getWalletBalance,
  subscribe,
} from "../services/subscriptionService";

const TABS = [
  { type: PLAN_TYPES.BROADCASTER, label: "Broadcaster", planLabel: "Go Live Plan" },
  { type: PLAN_TYPES.VIEWER, label: "Viewer", planLabel: "Viewer Plan" },
];
// The card payment activates the plan through Stripe's webhook, which can take a few seconds
const ACTIVATION_POLL_MS = 2000;
const ACTIVATION_POLL_ATTEMPTS = 10;

const SubscriptionPage = () => {
  const stripe = useStripe();
  const elements = useElements();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === PLAN_TYPES.VIEWER ? PLAN_TYPES.VIEWER : PLAN_TYPES.BROADCASTER;

  const [plans, setPlans] = useState({});
  const [current, setCurrent] = useState({});
  const [selected, setSelected] = useState(null);
  const [optionId, setOptionId] = useState(null);
  const [paymentMode, setPaymentMode] = useState("wallet");
  const [walletBalance, setWalletBalance] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadPlans = useCallback(
    (type) =>
      getPlans(type)
        .then((planList) => setPlans((prev) => ({ ...prev, [type]: planList })))
        .catch((error) => toast.error(error.message)),
    []
  );
  const loadCurrent = useCallback(
    (type) =>
      getCurrentSubscription(type)
        .then((subscription) => setCurrent((prev) => ({ ...prev, [type]: subscription })))
        .catch((error) => toast.error(error.message)),
    []
  );
  const reload = (type) => Promise.all([loadPlans(type), loadCurrent(type)]);

  // "Your Plan" shows both the Go Live and the Viewer plan
  useEffect(() => {
    TABS.forEach(({ type }) => loadCurrent(type));
  }, [loadCurrent]);

  useEffect(() => {
    setSelected(null);
    loadPlans(tab);
  }, [tab, loadPlans]);

  useEffect(() => {
    if (!selected) return;
    getWalletBalance()
      .then(setWalletBalance)
      .catch(() => setWalletBalance(null));
  }, [selected]);

  const tabPlans = plans[tab];
  const currentPlanId = current[tab]?.current_plan.id;
  const allPlans = tabPlans ? [...tabPlans.free_plans, ...tabPlans.plans] : [];

  const runAction = async (action, type) => {
    setBusy(true);
    try {
      const result = await action();
      if (result?.message) toast.success(result.message);
      await reload(type);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  // Moving to the free plan is a cancellation: the paid plan runs until the billing period ends
  const confirmCancel = (type) => {
    if (window.confirm("Your current plan stays active until the end of its billing period, then moves to the free plan. Continue?")) {
      runAction(() => cancelSubscription(type), type);
    }
  };

  const choosePlan = (plan) => {
    if (plan.is_free_tier) {
      confirmCancel(tab);
      return;
    }
    setSelected(plan);
    setOptionId(plan.pricing.find((option) => option.id !== currentPlanId)?.id || null);
  };

  const waitForActivation = async () => {
    for (let attempt = 0; attempt < ACTIVATION_POLL_ATTEMPTS; attempt += 1) {
      await new Promise((resolve) => setTimeout(resolve, ACTIVATION_POLL_MS));
      const subscription = await getCurrentSubscription(tab);
      if (subscription.current_plan.id === optionId) return true;
    }
    return false;
  };

  const handlePurchase = async () => {
    if (!optionId) return;
    if (paymentMode === "card" && (!stripe || !elements)) {
      toast.error("Card payments are not available right now");
      return;
    }

    setBusy(true);
    try {
      const result = await subscribe(optionId, paymentMode === "card" ? "payment_gateway" : "wallet");
      if (result.client_secret) {
        const { error, paymentIntent } = await stripe.confirmCardPayment(result.client_secret, {
          payment_method: { card: elements.getElement(CardElement) },
        });
        if (error) throw new Error(error.message);
        if (paymentIntent?.status !== "succeeded") throw new Error("The payment was not completed");
        toast.success(
          (await waitForActivation())
            ? `${selected.base_plan_name || selected.plan_name} is now active`
            : "Payment received. Your plan will be active in a moment."
        );
      } else {
        toast.success(result.message);
      }
      setSelected(null);
      await reload(tab);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  const renderYourPlan = ({ type, planLabel }) => {
    const subscription = current[type];
    if (!subscription) return null;
    const { current_plan: plan, upcoming_plan: upcoming, sessions_used, sessions_remaining } = subscription;

    return (
      <div key={type}>
        <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">{planLabel}</p>
        <PlanCard plan={plan} planType={type} light badge={planBadge(subscription)} />
        <div className="mt-4 space-y-3 text-sm text-gray-700">
          {type === PLAN_TYPES.BROADCASTER && (
            <p>
              Lives used this billing period:{" "}
              <span className="font-semibold">
                {sessions_remaining < 0 ? `${sessions_used} (unlimited)` : `${sessions_used} of ${sessions_used + sessions_remaining}`}
              </span>
            </p>
          )}
          {upcoming && (
            <div className="rounded-xl bg-[#EBF8ED] p-4 text-[#095228]">
              {upcoming.is_free_tier ? "Cancelled. " : ""}
              Changes to <span className="font-semibold">{upcoming.plan_name}</span> on {formatDateOnly(upcoming.effective_date)}.
              <button
                type="button"
                disabled={busy}
                onClick={() => runAction(() => cancelScheduledChange(type), type)}
                className="ml-2 font-semibold underline disabled:opacity-60"
              >
                {upcoming.is_free_tier ? "Keep my plan" : "Undo change"}
              </button>
            </div>
          )}
          {!plan.is_free_tier && !upcoming && (
            <button
              type="button"
              disabled={busy}
              onClick={() => confirmCancel(type)}
              className="rounded-full border border-gray-300 px-5 py-2 font-semibold text-gray-700 disabled:opacity-60"
            >
              Cancel subscription
            </button>
          )}
          {!plan.is_free_tier && (
            <p className="text-xs text-gray-500">
              Renewals are charged from your wallet.{" "}
              <Link to="/wallet" className="font-semibold text-[#095228] underline">
                Add money
              </Link>
            </p>
          )}
          {type === PLAN_TYPES.VIEWER && plan.replay_enabled && (
            <Link
              to="/past-lives"
              className="inline-block rounded-full bg-gradient-to-r from-[#08AEEA] to-[#1DE78B] px-5 py-2 font-semibold text-white"
            >
              Watch Past Lives
            </Link>
          )}
        </div>
      </div>
    );
  };

  const renderPlans = () => (
    <section>
      <h2 className="mb-4 text-2xl font-bold text-[#0B4F2A]">Choose a Plan</h2>
      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4 items-start">
        {allPlans.map((plan) => {
          const isCurrent = plan.is_free_tier ? plan.is_active : plan.is_current_base_plan;
          // On the current paid plan only its other billing periods can be chosen
          const canChange = !plan.is_free_tier && plan.pricing.some((option) => option.id !== currentPlanId);
          return (
            <PlanCard
              key={plan.id}
              plan={plan}
              planType={tab}
              light={isCurrent}
              actionLabel={!isCurrent ? "Choose Plan" : canChange ? "Change Billing Period" : "Current Plan"}
              actionDisabled={busy || (isCurrent ? !canChange : !plan.is_free_tier && !plan.pricing.length)}
              onAction={() => choosePlan(plan)}
            />
          );
        })}
      </div>
      {!tabPlans.plans.length && <p className="mt-4 text-sm text-gray-500">More plans are coming soon.</p>}
    </section>
  );

  const renderCheckout = () => (
    <section>
      <button type="button" onClick={() => setSelected(null)} className="mb-4 flex items-center gap-2 text-sm font-semibold text-[#095228]">
        <FaArrowLeft /> All plans
      </button>
      <div className="grid gap-8 md:grid-cols-[minmax(0,360px)_minmax(0,320px)] items-start">
        <PlanCard plan={selected} planType={tab} light />

        <div className="space-y-4 md:pt-24">
          {selected.pricing.map((option) => (
            <button
              key={option.id}
              type="button"
              disabled={option.id === currentPlanId}
              onClick={() => setOptionId(option.id)}
              className={`flex w-full items-center justify-between rounded-[20px] border-2 bg-[#2B2B2B] px-6 py-4 text-white disabled:cursor-not-allowed disabled:opacity-50 ${
                optionId === option.id ? "border-[#F2D21B]" : "border-transparent"
              }`}
            >
              <span className="flex items-center gap-3 text-base">
                {option.name.split(" - ").pop()}
                {option.id === currentPlanId ? (
                  <span className="rounded-full bg-[#1DE78B] px-3 py-0.5 text-[11px] font-semibold text-[#0B2A1A]">Current</span>
                ) : (
                  option.label && (
                    <span className="rounded-full bg-[#E91E63] px-3 py-0.5 text-[11px] font-semibold">{option.label}</span>
                  )
                )}
              </span>
              <span className="text-xl font-semibold">{option.price}</span>
            </button>
          ))}

          <div className="flex gap-3 text-sm">
            {[
              ["wallet", "Wallet"],
              ["card", "Card"],
            ].map(([mode, label]) => (
              <button
                key={mode}
                type="button"
                onClick={() => setPaymentMode(mode)}
                className={`flex-1 rounded-full border px-4 py-2 font-semibold ${
                  paymentMode === mode ? "border-[#095228] bg-[#EBF8ED] text-[#095228]" : "border-gray-300 text-gray-600"
                }`}
              >
                Pay with {label}
                {mode === "wallet" && paymentMode === "wallet" && walletBalance !== null && (
                  <span className="block text-[11px] font-normal">Balance: ${walletBalance.toFixed(2)}</span>
                )}
              </button>
            ))}
          </div>
          {paymentMode === "card" ? (
            <div className="rounded-xl border border-gray-300 bg-white p-3">
              <CardElement options={{ hidePostalCode: true, style: { base: { fontSize: "15px" } } }} />
            </div>
          ) : (
            <p className="text-xs text-gray-500">
              The plan price is deducted from your wallet.{" "}
              <Link to="/wallet" className="font-semibold text-[#095228] underline">
                Add money
              </Link>
            </p>
          )}

          <hr className="border-gray-300" />

          <button
            type="button"
            disabled={busy || !optionId}
            onClick={handlePurchase}
            className="flex w-full items-center justify-center gap-3 rounded-full border-2 border-[#095228] bg-[#EBF8ED] px-6 py-3 font-semibold text-[#095228] disabled:opacity-60"
          >
            <FaCrown className="text-xl" />
            {busy
              ? "Please wait..."
              : selected.is_current_base_plan
                ? "Change Billing Period"
                : `Upgrade to ${selected.base_plan_name || selected.plan_name}`}
          </button>
          <p className="text-center text-sm">
            <Link to="/terms-conditions" className="font-semibold text-[#095228]">
              Terms & Conditions
            </Link>
          </p>
        </div>
      </div>
    </section>
  );

  return (
    <div className="dashboard w-full min-h-screen block lg:grid lg:grid-cols-[auto_minmax(0,1fr)]">
      <div className="h-full">
        <Leftsidebar />
      </div>

      <main className="flex-1 min-w-0 overflow-x-hidden p-4 md:p-6 md:pl-8">
        <LeftResponsivesidebar />
        <Headmenu />

        <div className="mt-4 space-y-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h1 className="text-2xl font-bold text-gray-900">Subscription</h1>
            {!selected && (
              <div className="flex rounded-full bg-[#F4F4F4] p-1 text-sm font-semibold">
                {TABS.map(({ type, label }) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setSearchParams({ tab: type })}
                    className={`rounded-full px-5 py-2 ${tab === type ? "bg-[#095228] text-white" : "text-gray-600"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {selected ? (
            renderCheckout()
          ) : (
            <>
              <section>
                <h2 className="mb-4 text-2xl font-bold text-[#0B4F2A]">Your Plan</h2>
                <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4 items-start">{TABS.map(renderYourPlan)}</div>
              </section>
              {tabPlans ? renderPlans() : <p className="text-sm text-gray-500">Loading plans...</p>}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default SubscriptionPage;
